// Progressive loader for Admin Data (admin-data endpoint)
// Loads month-by-month when the selected range spans more than one month

import api from './adminData'
import mergeSummaryByDoctor from '@/core/utils/mergeSummaryByDoctor'
import { extractPaginatorTotals, loadGlobalPage, DEFAULT_PER_PAGE } from '@/core/utils/progressivePagination'
import {
  MIN_BLOCK_DURATION_MS,
  groupRanges,
  runWithMinDuration,
} from '@/core/utils/requestPacing'
import {
  toNum,
  round2,
  weightedAverageByVpd,
} from '@/core/utils/adminDataMetrics'

class ProgressiveAdminDataLoader {
  constructor() {
    this.isLoading = false
    this.currentProgress = 0
    this.totalMonths = 0
    this.onProgressCallback = null
    this.onCompleteCallback = null
    this.onErrorCallback = null
    this.abortRequested = false
  }

  requestAbort() {
    this.abortRequested = true
  }

  reset() {
    this.isLoading = false
    this.currentProgress = 0
    this.totalMonths = 0
    this.onProgressCallback = null
    this.onCompleteCallback = null
    this.onErrorCallback = null
    this.abortRequested = false
  }

  /** Solo flags de ejecución; no borra callbacks (evita romper progreso de Excel). */
  resetFlagsOnly() {
    this.isLoading = false
    this.abortRequested = false
    this.currentProgress = 0
    this.totalMonths = 0
  }

  /** Espera a que termine una carga en curso (p. ej. Search antes de Excel). */
  async waitUntilIdle(maxMs = 15000) {
    if (!this.isLoading) {
      this.abortRequested = false
      return
    }
    this.abortRequested = true
    const started = Date.now()
    while (this.isLoading && Date.now() - started < maxMs) {
      await new Promise(r => setTimeout(r, 50))
    }
    this.isLoading = false
    this.abortRequested = false
  }

  emitProgress(payload) {
    if (this.onProgressCallback) {
      this.onProgressCallback(payload)
    }
  }

  yieldToUi() {
    return new Promise(resolve => setTimeout(resolve, 0))
  }

  // Parse 'MM-DD-YYYY HH:mm:ss' into Date
  parseDateTimeString(dateTimeStr) {
    // Expected like '8-09-2024 09:00:00' or '08-09-2024 09:00:00'
    if (!dateTimeStr || typeof dateTimeStr !== 'string') return new Date(NaN)
    const [datePart, timePart = '00:00:00'] = dateTimeStr.split(' ')
    const [m, d, y] = datePart.split('-').map(Number)
    const [hh, mm, ss] = timePart.split(':').map(Number)
    // new Date(year, monthIndex, day, hours, minutes, seconds)
    return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, ss || 0)
  }

  // Format Date + time string to 'M-D-YYYY HH:mm:ss'
  formatForFilter(dateObj, timeStr = '00:00:00') {
    const y = dateObj.getFullYear()
    const m = dateObj.getMonth() + 1
    const d = dateObj.getDate()
    return `${m}-${d}-${y} ${timeStr}`
  }

  calculateMonthsBetween(startDateTime, endDateTime) {
    const start = this.parseDateTimeString(startDateTime)
    const end = this.parseDateTimeString(endDateTime)
    const yearDiff = end.getFullYear() - start.getFullYear()
    const monthDiff = end.getMonth() - start.getMonth()
    return yearDiff * 12 + monthDiff + 1
  }

  generateMonthlyRanges(startDateTime, endDateTime) {
    const start = this.parseDateTimeString(startDateTime)
    const end = this.parseDateTimeString(endDateTime)

    const startTime = startDateTime.split(' ')[1] || '00:00:00'
    const endTime = endDateTime.split(' ')[1] || '23:59:59'

    const ranges = []
    let current = new Date(start.getFullYear(), start.getMonth(), 1)

    while (current <= end) {
      const isFirstMonth =
        current.getFullYear() === start.getFullYear() &&
        current.getMonth() === start.getMonth()
      const isLastMonth =
        current.getFullYear() === end.getFullYear() &&
        current.getMonth() === end.getMonth()

      const monthStart = new Date(current.getFullYear(), current.getMonth(), 1)
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0)

      const rangeStartDate = isFirstMonth ? start : monthStart
      const rangeEndDate = isLastMonth ? end : monthEnd

      const rangeStart = this.formatForFilter(
        rangeStartDate,
        isFirstMonth ? startTime : '00:00:00'
      )
      const rangeEnd = this.formatForFilter(
        rangeEndDate,
        isLastMonth ? endTime : '23:59:59'
      )

      ranges.push({
        start: rangeStart,
        end: rangeEnd,
        month: current.getMonth() + 1,
        year: current.getFullYear(),
      })

      current.setMonth(current.getMonth() + 1)
    }

    return ranges
  }

  /** Guarda rangos y totales de la última carga progresiva para poder paginarla después. */
  rememberBlocks(ranges, monthlyDataArray, filters) {
    this.lastFilters = { ...filters }
    this.lastBlocks = ranges.map((range, index) => ({
      range,
      totals: extractPaginatorTotals(monthlyDataArray[index]),
    }))
  }

  /**
   * Página global de la tabla de consultas y/o de las campañas de la última carga progresiva.
   * @param {number} page
   * @param {{ rows?: boolean, campaigns?: boolean }} targets
   */
  async loadPage(page, targets) {
    if (!this.lastBlocks?.length) throw new Error('NO_PROGRESSIVE_DATA')
    return loadGlobalPage(
      this.lastBlocks,
      page,
      async (range, localPage) => {
        const response = await api.getAdminDataPaginated(
          localPage,
          { ...this.lastFilters, start_date: range.start, end_date: range.end, data_excel: false },
          { throwOnError: true }
        )
        return response?.data || {}
      },
      targets
    )
  }

  async loadMonthData(dateRange, baseFilters, dataExcel = false) {
    const payload = {
      ...baseFilters,
      start_date: dateRange.start,
      end_date: dateRange.end,
      data_excel: dataExcel,
    }
    // Un mes fallido debe cortar la carga: tomarlo como vacío deja totales incompletos sin aviso
    const response = await api.getAdminData(payload, { throwOnError: true })
    if (!response) return {}
    return response.data != null ? response.data : response
  }

  // Generic deep merge: numbers summed, arrays concatenated, objects merged
  deepMergeAdminData(target, source) {
    if (target === null || target === undefined) return source
    if (source === null || source === undefined) return target

    // Try to coerce numeric strings
    const isNumericLike = v =>
      typeof v === 'number' ||
      (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)))

    if (isNumericLike(target) && isNumericLike(source)) {
      return Number(target) + Number(source)
    }

    if (Array.isArray(target) && Array.isArray(source)) {
      return target.concat(source)
    }

    if (typeof target === 'object' && typeof source === 'object') {
      const result = Array.isArray(target) ? [...target] : { ...target }
      Object.keys(source).forEach(key => {
        if (result[key] === undefined) {
          result[key] = source[key]
        } else {
          result[key] = this.deepMergeAdminData(result[key], source[key])
        }
      })
      return result
    }

    // Fallback: prefer source (newer month)
    return source
  }

  combineMonthlyData(monthlyDataArray) {
    if (!Array.isArray(monthlyDataArray) || monthlyDataArray.length === 0) return {}

    // Paso 1: merge profundo general
    let combined = JSON.parse(JSON.stringify(monthlyDataArray[0]))
    for (let i = 1; i < monthlyDataArray.length; i++) {
      combined = this.deepMergeAdminData(combined, monthlyDataArray[i])
    }

    // Paso 2: combinaciones específicas de dominio
    // 2.1 COUNT_PER_TYPE: sumar por prefix
    try {
      const allCpt = monthlyDataArray
        .map(m => (Array.isArray(m?.COUNT_PER_TYPE) ? m.COUNT_PER_TYPE : []))
        .flat()
      if (allCpt.length) {
        const map = new Map()
        allCpt.forEach(item => {
          const key = String(item?.prefix ?? '')
          const prev = map.get(key) || 0
          const add = Number(item?.count ?? 0)
          map.set(key, prev + add)
        })
        combined.COUNT_PER_TYPE = Array.from(map.entries()).map(([prefix, count]) => ({
          prefix,
          count,
        }))
      }
    } catch (_) {}

    // 2.2 Paginaciones genéricas: ROWS_PAGINATE y CUPONS_PAGINATE dentro y fuera de CAMPANA
    const combinePaginate = (p1, p2) => {
      if (!p1) return p2 || null
      if (!p2) return p1
      const d1 = Array.isArray(p1.data) ? p1.data : []
      const d2 = Array.isArray(p2.data) ? p2.data : []
      const mergedData = d1.concat(d2)
      // Debe coincidir con el per_page del backend para que la página N pedida después sea la correcta
      const per_page = Number(p1.per_page || p2.per_page) || DEFAULT_PER_PAGE
      // Importante: usar la suma de los 'total' reportados por backend (no el tamaño de data)
      const total = (Number(p1.total || 0) || 0) + (Number(p2.total || 0) || 0)
      const last_page = per_page > 0 ? Math.ceil(total / per_page) : 1
      const path = p1.path || p2.path || ''
      const makeUrl = page => (path ? `${path}?page=${page}` : null)
      return {
        current_page: 1,
        data: mergedData.slice(0, per_page),
        first_page_url: makeUrl(1),
        from: total > 0 ? 1 : 0,
        last_page,
        last_page_url: makeUrl(last_page),
        links: [],
        next_page_url: null,
        path,
        per_page,
        prev_page_url: null,
        to: Math.min(total, per_page),
        total,
      }
    }

    try {
      // ROWS_PAGINATE en raíz
      const rowsList = monthlyDataArray.map(m => m?.ROWS_PAGINATE).filter(Boolean)
      if (rowsList.length) {
        combined.ROWS_PAGINATE = rowsList.reduce(
          (acc, cur) => combinePaginate(acc, cur),
          null
        )
        combined.ALL_CONSULT_ROWS = monthlyDataArray.flatMap(m => {
          const rows = m?.ROWS_PAGINATE?.data
          return Array.isArray(rows) ? rows : []
        })
      }
    } catch (_) {}

    try {
      // CUPONS_PAGINATE en raíz
      const cuponsList = monthlyDataArray.map(m => m?.CUPONS_PAGINATE).filter(Boolean)
      if (cuponsList.length) {
        combined.CUPONS_PAGINATE = cuponsList.reduce(
          (acc, cur) => combinePaginate(acc, cur),
          null
        )
      }
    } catch (_) {}

    // 2.3 CAMPAIGN_CUPONS: combinar por llave de descuento
    try {
      const allKeys = new Set()
      monthlyDataArray.forEach(m => {
        const cc = m?.CAMPAIGN_CUPONS
        if (cc && typeof cc === 'object') Object.keys(cc).forEach(k => allKeys.add(k))
      })
      if (allKeys.size) {
        const result = {}
        allKeys.forEach(key => {
          let bucket = null
          monthlyDataArray.forEach(m => {
            const entry = m?.CAMPAIGN_CUPONS?.[key]
            if (entry) {
              if (!bucket) bucket = JSON.parse(JSON.stringify(entry))
              else {
                // TOTAL_DISCOUNT puede venir como string
                const td1 = Number(bucket?.TOTAL_DISCOUNT || 0)
                const td2 = Number(entry?.TOTAL_DISCOUNT || 0)
                bucket.TOTAL_DISCOUNT = td1 + td2
                bucket.CUPONS_PAGINATE = combinePaginate(
                  bucket.CUPONS_PAGINATE,
                  entry.CUPONS_PAGINATE
                )
              }
            }
          })
          // Ajuste clave: el total de cupones redimidos se deriva del TOTAL_DISCOUNT
          // dividido entre el valor de la campaña (key). Evita errores por paginación.
          const campaignValue = Number(key)
          if (bucket && bucket.CUPONS_PAGINATE && campaignValue > 0) {
            const sumDiscount = Number(bucket.TOTAL_DISCOUNT || 0)
            const derivedTotal = Math.round(sumDiscount / campaignValue)
            bucket.CUPONS_PAGINATE.total = derivedTotal
          }
          result[key] = bucket
        })
        combined.CAMPAIGN_CUPONS = result
      }
    } catch (_) {}

    // 2.4 REFERENCES: sumar por categoría manteniendo el formato "Label: value"
    try {
      const aggregateRefs = selector => {
        // Recolectar dinámicamente todas las labels que aparecen en los datos
        const allLabelsSet = new Set()
        monthlyDataArray.forEach(m => {
          const arr = m?.REFERENCES?.[selector]
          if (Array.isArray(arr)) {
            arr.forEach(it => {
              const text = String(it?.references || '')
              const idx = text.indexOf(':')
              if (idx !== -1) {
                const label = text.slice(0, idx).trim()
                if (label) {
                  allLabelsSet.add(label)
                }
              }
            })
          }
        })
        
        // Convertir a array ordenado para mantener consistencia
        const allLabels = Array.from(allLabelsSet).sort()
        
        // Inicializar el Map con todas las labels encontradas
        const sums = new Map(allLabels.map(l => [l, 0]))
        
        // Procesar todos los datos y sumar valores
        monthlyDataArray.forEach(m => {
          const arr = m?.REFERENCES?.[selector]
          if (Array.isArray(arr)) {
            arr.forEach(it => {
              const text = String(it?.references || '')
              const idx = text.indexOf(':')
              if (idx !== -1) {
                const label = text.slice(0, idx).trim()
                const num = Number(text.slice(idx + 1).trim() || 0)
                if (!isNaN(num) && label) {
                  // Si la label no existe en el Map, agregarla
                  if (!sums.has(label)) {
                    sums.set(label, 0)
                    allLabels.push(label)
                    allLabels.sort()
                  }
                  sums.set(label, (sums.get(label) || 0) + num)
                }
              }
            })
          }
        })
        
        // Retornar todas las labels encontradas (ordenadas) con sus valores
        return allLabels.map(l => ({ references: `${l}: ${sums.get(l) || 0}` }))
      }
      const nc = aggregateRefs('NC')
      const np = aggregateRefs('NP')
      if (!combined.REFERENCES) combined.REFERENCES = {}
      combined.REFERENCES.NC = nc
      combined.REFERENCES.NP = np
    } catch (_) {}

    try {
      delete combined.SUMMARY_BY_DOCTOR
      const byDoctor = mergeSummaryByDoctor(monthlyDataArray)
      if (byDoctor.length) combined.SUMMARY_BY_DOCTOR = byDoctor
    } catch (_) {}

    // DATA_EXCEL: concatenación directa (más rápida que deep merge con muchas filas)
    try {
      const excelRows = monthlyDataArray.flatMap(m =>
        Array.isArray(m?.DATA_EXCEL) ? m.DATA_EXCEL : []
      )
      if (excelRows.length) combined.DATA_EXCEL = excelRows
    } catch (_) {}

    // 2.5 PROMEDIOS ponderados por su VPD (nunca promedio de promedios)
    try {
      Object.assign(combined, this.combineWeightedAverages(monthlyDataArray))
    } catch (_) {}

    return combined
  }

  /**
   * AVG_* ponderados por volumen: el promedio simple entre meses distorsiona
   * cuando un mes tiene muchas más visitas que otro.
   */
  combineWeightedAverages(monthlyDataArray) {
    // Insurance reporta los mismos tipos con prefijo INS-NP / INS-NC
    const countOf = (month, prefixes) => {
      const list = Array.isArray(month?.COUNT_PER_TYPE) ? month.COUNT_PER_TYPE : []
      return list
        .filter(item => prefixes.includes(String(item?.prefix ?? '')))
        .reduce((sum, item) => sum + toNum(item?.count), 0)
    }

    let incomeTotal = 0
    let vpdTotal = 0
    let npWeighted = 0
    let npCount = 0
    let ncWeighted = 0
    let ncCount = 0

    monthlyDataArray.forEach(month => {
      incomeTotal += toNum(month?.CLINIC_INCOME_TOTAL)
      vpdTotal += toNum(month?.TOTAL_VPD)

      const np = countOf(month, ['N/P', 'INS-NP'])
      npWeighted += toNum(month?.AVG_NP) * np
      npCount += np

      const nc = countOf(month, ['N/C', 'INS-NC'])
      ncWeighted += toNum(month?.AVG_NC) * nc
      ncCount += nc
    })

    const avgNp = npCount > 0 ? npWeighted / npCount : 0
    const avgNc = ncCount > 0 ? ncWeighted / ncCount : 0

    const out = {
      AVG_TOTAL: vpdTotal > 0 ? round2(incomeTotal / vpdTotal) : 0,
      AVG_NP: round2(avgNp),
      AVG_NC: round2(avgNc),
      AVG_NC_NP: round2((avgNc + avgNp) / 2),
      // Regular: ponderar por VPD / NON_PROFIT_VPD
      PROFIT_AVG: weightedAverageByVpd(monthlyDataArray, 'PROFIT_AVG', 'VPD'),
      NON_PROFIT_AVG: weightedAverageByVpd(
        monthlyDataArray,
        'NON_PROFIT_AVG',
        'NON_PROFIT_VPD'
      ),
    }

    // Seguro (si el backend ya manda INSURANCE_*): ponderar por INSURANCE_VPD
    const hasInsurance = monthlyDataArray.some(
      m => m?.INSURANCE_AVG != null || m?.INSURANCE_VPD != null
    )
    if (hasInsurance) {
      out.INSURANCE_AVG = weightedAverageByVpd(
        monthlyDataArray,
        'INSURANCE_AVG',
        'INSURANCE_VPD'
      )
      out.INSURANCE_VPD = monthlyDataArray.reduce(
        (s, m) =>
          s + toNum(m?.INSURANCE_VPD != null ? m.INSURANCE_VPD : m?.VPD),
        0
      )
    }

    return out
  }

  async loadProgressiveData(filters, dataExcel = false) {
    const { start_date, end_date } = filters || {}
    const monthsBetween = this.calculateMonthsBetween(start_date, end_date)

    if (monthsBetween <= 1) {
      const resp = await api.getAdminData(
        { ...filters, data_excel: dataExcel },
        { throwOnError: true }
      )
      if (!resp) return {}
      return resp.data != null ? resp.data : resp
    }

    this.isLoading = true
    this.abortRequested = false
    const monthlyRanges = groupRanges(this.generateMonthlyRanges(start_date, end_date))
    this.totalMonths = monthlyRanges.length
    this.currentProgress = 0

    try {
      const monthlyDataArray = []

      this.emitProgress({
        current: 0,
        total: this.totalMonths,
        percentage: 0,
        currentMonth: monthlyRanges[0]?.month,
        currentYear: monthlyRanges[0]?.year,
        phase: 'loading',
      })
      await this.yieldToUi()

      for (let i = 0; i < monthlyRanges.length; i++) {
        if (this.abortRequested) throw new Error('ABORTED_BY_USER')

        const range = monthlyRanges[i]

        this.emitProgress({
          current: i,
          total: this.totalMonths,
          percentage: Math.round((i / this.totalMonths) * 100),
          currentMonth: range.month,
          currentYear: range.year,
          phase: 'loading',
        })
        await this.yieldToUi()

        const isLastMonth = i === monthlyRanges.length - 1
        const monthData = await runWithMinDuration(
          () => this.loadMonthData(range, filters, dataExcel),
          isLastMonth ? 0 : MIN_BLOCK_DURATION_MS
        )
        monthlyDataArray.push(monthData)

        this.currentProgress = i + 1
        this.emitProgress({
          current: this.currentProgress,
          total: this.totalMonths,
          percentage: Math.round((this.currentProgress / this.totalMonths) * 100),
          currentMonth: range.month,
          currentYear: range.year,
          phase: 'loading',
        })
        await this.yieldToUi()
      }

      this.emitProgress({
        current: this.totalMonths,
        total: this.totalMonths,
        percentage: 100,
        currentMonth: monthlyRanges[monthlyRanges.length - 1]?.month,
        currentYear: monthlyRanges[monthlyRanges.length - 1]?.year,
        phase: 'combining',
      })
      await this.yieldToUi()

      this.rememberBlocks(monthlyRanges, monthlyDataArray, filters)
      const combined = this.combineMonthlyData(monthlyDataArray)
      if (this.onCompleteCallback) this.onCompleteCallback(combined)
      return combined
    } catch (error) {
      if (this.onErrorCallback) this.onErrorCallback(error)
      throw error
    } finally {
      this.resetFlagsOnly()
    }
  }

  // Carga progresiva SOLO para overlays específicos, sin tocar otras métricas
  async loadProgressiveOverlays(filters) {
    const { start_date, end_date } = filters || {}
    const monthsBetween = this.calculateMonthsBetween(start_date, end_date)

    // Si es un mes o menos, simplemente devolver estructura compatible a partir de 1 llamada
    if (monthsBetween <= 1) {
      const resp = await api.getAdminData({ ...filters, data_excel: false })
      const data = resp?.data || {}
      const overlay = {}
      if (data.ROWS_PAGINATE) overlay.ROWS_PAGINATE = data.ROWS_PAGINATE
      if (data.CAMPAIGN_CUPONS) overlay.CAMPAIGN_CUPONS = data.CAMPAIGN_CUPONS
      return overlay
    }

    this.isLoading = true
    this.abortRequested = false
    const monthlyRanges = groupRanges(this.generateMonthlyRanges(start_date, end_date))
    this.totalMonths = monthlyRanges.length
    this.currentProgress = 0

    try {
      const monthlyDataArray = []
      for (let i = 0; i < monthlyRanges.length; i++) {
        if (this.abortRequested) throw new Error('ABORTED_BY_USER')
        const range = monthlyRanges[i]
        const isLastMonth = i === monthlyRanges.length - 1
        const monthData = await runWithMinDuration(
          () => this.loadMonthData(range, filters),
          isLastMonth ? 0 : MIN_BLOCK_DURATION_MS
        )
        monthlyDataArray.push(monthData)

        this.currentProgress = i + 1
        if (this.onProgressCallback) {
          this.onProgressCallback({
            current: this.currentProgress,
            total: this.totalMonths,
            percentage: Math.round((this.currentProgress / this.totalMonths) * 100),
            currentMonth: range.month,
            currentYear: range.year,
          })
        }
      }

      // Construir overlay: solo ROWS_PAGINATE y CAMPAIGN_CUPONS
      const overlay = {}

      // ROWS_PAGINATE
      try {
        const rowsList = monthlyDataArray.map(m => m?.ROWS_PAGINATE).filter(Boolean)
        if (rowsList.length) {
          overlay.ROWS_PAGINATE = rowsList.reduce(
            (acc, cur) => this.combinePaginatePublic(acc, cur),
            null
          )
        }
      } catch (_) {}

      // CAMPAIGN_CUPONS (sumar descuentos y derivar totales por campaña)
      try {
        const allKeys = new Set()
        monthlyDataArray.forEach(m => {
          const cc = m?.CAMPAIGN_CUPONS
          if (cc && typeof cc === 'object') Object.keys(cc).forEach(k => allKeys.add(k))
        })
        if (allKeys.size) {
          const result = {}
          allKeys.forEach(key => {
            let bucket = null
            monthlyDataArray.forEach(m => {
              const entry = m?.CAMPAIGN_CUPONS?.[key]
              if (entry) {
                if (!bucket) bucket = JSON.parse(JSON.stringify(entry))
                else {
                  const td1 = Number(bucket?.TOTAL_DISCOUNT || 0)
                  const td2 = Number(entry?.TOTAL_DISCOUNT || 0)
                  bucket.TOTAL_DISCOUNT = td1 + td2
                  bucket.CUPONS_PAGINATE = this.combinePaginatePublic(
                    bucket.CUPONS_PAGINATE,
                    entry.CUPONS_PAGINATE
                  )
                }
              }
            })
            const campaignValue = Number(key)
            if (bucket && bucket.CUPONS_PAGINATE && campaignValue > 0) {
              const sumDiscount = Number(bucket.TOTAL_DISCOUNT || 0)
              const derivedTotal = Math.round(sumDiscount / campaignValue)
              bucket.CUPONS_PAGINATE.total = derivedTotal
            }
            result[key] = bucket
          })
          overlay.CAMPAIGN_CUPONS = result
        }
      } catch (_) {}

      this.isLoading = false
      return overlay
    } catch (error) {
      this.isLoading = false
      if (this.onErrorCallback) this.onErrorCallback(error)
      throw error
    }
  }

  // Exponer combinador de paginación para uso interno del overlay
  combinePaginatePublic(p1, p2) {
    return (function self(ctx, a, b) {
      const d1 = Array.isArray(a?.data) ? a.data : []
      const d2 = Array.isArray(b?.data) ? b.data : []
      const mergedData = d1.concat(d2)
      const per_page = 5
      const total = (Number(a?.total || 0) || 0) + (Number(b?.total || 0) || 0)
      const path = a?.path || b?.path || ''
      const makeUrl = page => (path ? `${path}?page=${page}` : null)
      const last_page = per_page > 0 ? Math.ceil(total / per_page) : 1
      return {
        current_page: 1,
        data: mergedData.slice(0, per_page),
        first_page_url: makeUrl(1),
        from: total > 0 ? 1 : 0,
        last_page,
        last_page_url: makeUrl(last_page),
        links: [],
        next_page_url: null,
        path,
        per_page,
        prev_page_url: null,
        to: Math.min(total, per_page),
        total,
      }
    })(this, p1, p2)
  }

  onProgress(callback) {
    this.onProgressCallback = callback
  }
  onComplete(callback) {
    this.onCompleteCallback = callback
  }
  onError(callback) {
    this.onErrorCallback = callback
  }
}

const progressiveAdminDataLoader = new ProgressiveAdminDataLoader()
export default progressiveAdminDataLoader
